import type { FC } from "react";
import React from "react";

import cn from "classnames";

import { Content, ContentsView } from "../../../Content";
import { Icon } from "../../../Icon";
import Banner from "../../Banner";

import type { TGlyphSizes } from "../../../../assets/types/icons";
import type { BannerProps } from "../../types";

import classes from "./BannerAlerts.styl";

export type BannerDefaultAlertProps = {
    /** Заголовок */
    title: string;
    /** Подзаголовок */
    subtitle: string;
    view: Exclude<BannerProps["view"], "custom">;
    icon: Extract<TGlyphSizes, { size: "24" }>["icon"];
};

/**
 * Алерт баннер со статусной моделью.
 *
 * <a href="https://www.figma.com/design/SZCPsHnuqBIWlYSyfmq0j1/🧩-Sheets-%26-Modules-%26-Wrappers?node-id=689-101838&node-type=frame&m=dev" target="_blank">Пресеты</a>
 * @constructor
 */
export const BannerDefaultAlert: FC<BannerDefaultAlertProps> = ({
    title,
    subtitle,
    icon,
    view,
}) => {
    const isSupportView = view && view !== "primary" && view !== "secondary";
    const iconClasses = cn(
        classes["icon"],
        isSupportView && classes[`__view-${view}`],
    );

    return (
        <Banner view={view} data-tsid="banner-alerts-test-id">
            <div className={classes["container"]}>
                <Icon className={iconClasses} size="24" icon={icon} />

                <ContentsView>
                    <ContentsView.Row>
                        <ContentsView.Content>
                            <Content.Text size="15">{title}</Content.Text>
                        </ContentsView.Content>
                    </ContentsView.Row>
                    <ContentsView.Row>
                        <ContentsView.Content>
                            <Content.Text size="13" view="secondary">
                                {subtitle}
                            </Content.Text>
                        </ContentsView.Content>
                    </ContentsView.Row>
                </ContentsView>
            </div>
        </Banner>
    );
};

BannerDefaultAlert.displayName = "BannerDefaultAlert";

type BannerAlertProps = Pick<BannerDefaultAlertProps, "title" | "subtitle">;

export const BannerAlertPositive: FC<BannerAlertProps> = (props) => {
    return (
        <BannerDefaultAlert
            view="positive"
            icon="glyph_check_circle_on_24+"
            {...props}
        />
    );
};

BannerAlertPositive.displayName = "BannerAlertPositive";

export const BannerAlertNegative: FC<BannerAlertProps> = (props) => {
    return (
        <BannerDefaultAlert
            view="negative"
            icon="glyph_alert_circle_24+"
            {...props}
        />
    );
};

BannerAlertNegative.displayName = "BannerAlertNegative";

export const BannerAlertInfo: FC<BannerAlertProps> = (props) => {
    return (
        <BannerDefaultAlert
            view="info"
            icon="glyph_info_circle_24+"
            {...props}
        />
    );
};

BannerAlertInfo.displayName = "BannerAlertInfo";

export const BannerAlertAttention: FC<BannerAlertProps> = (props) => {
    return (
        <BannerDefaultAlert
            view="attention"
            icon="glyph_info_circle_24+"
            {...props}
        />
    );
};

BannerAlertAttention.displayName = "BannerAlertAttention";
